/* tslint:disable */
/* eslint-disable */
import { Injectable } from '@angular/core';
import { HttpClient, HttpResponse, HttpContext } from '@angular/common/http';
import { BaseService } from '../base-service';
import { ApiConfiguration } from '../api-configuration';
import { StrictHttpResponse } from '../strict-http-response';
import { RequestBuilder } from '../request-builder';
import { Observable } from 'rxjs';
import { map, filter } from 'rxjs/operators';

import { Extraction } from '../models/extraction';
import { ExtractionList } from '../models/extraction-list';
import { ExtractionModels } from '../models/extraction-models';
import { ExtractionRepresentative } from '../models/extraction-representative';
import { ExtractionState } from '../models/extraction-state';
import { RerunExtractionRequest } from '../models/rerun-extraction-request';
import { SubmitExtractionRequest } from '../models/submit-extraction-request';


/**
 * Invoice extractions API (an AI model reads a client's invoice PDF)
 */
@Injectable({
  providedIn: 'root',
})
export class ExtractionsService extends BaseService {
  constructor(
    config: ApiConfiguration,
    http: HttpClient
  ) {
    super(config, http);
  }

  /**
   * Path part for operation listExtractions
   */
  static readonly ListExtractionsPath = '/invoices/extractions';

  /**
   * List extractions.
   *
   * Extractions, newest upload first: by default the caller's own, with
   * `scope=everyone` every user's, and with `state` only those in the
   * given states. Every user with the permission can list and open
   * everyone's extractions, so one can finish a colleague's.
   *
   * Requires the `invoices:extract` permission.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `listExtractions()` instead.
   *
   * This method doesn't expect any request body.
   */
  listExtractions$Response(params?: {

    /**
     * Whose extractions: &#x60;mine&#x60; (uploaded by the caller, the default)
     * or &#x60;everyone&#x60;.
     */
    scope?: 'mine' | 'everyone';

    /**
     * Only extractions in these states; every state when omitted.
     * Repeat the parameter for several (&#x60;state&#x3D;ready_for_review&amp;state&#x3D;failed&#x60;).
     */
    state?: Array<ExtractionState>;
    context?: HttpContext
  }
): Observable<StrictHttpResponse<ExtractionList>> {

    const rb = new RequestBuilder(this.rootUrl, ExtractionsService.ListExtractionsPath, 'get');
    if (params) {
      rb.query('scope', params.scope, {});
      rb.query('state', params.state, {"style":"form","explode":true});
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json',
      context: params?.context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<ExtractionList>;
      })
    );
  }

  /**
   * List extractions.
   *
   * Extractions, newest upload first: by default the caller's own, with
   * `scope=everyone` every user's, and with `state` only those in the
   * given states. Every user with the permission can list and open
   * everyone's extractions, so one can finish a colleague's.
   *
   * Requires the `invoices:extract` permission.
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `listExtractions$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  listExtractions(params?: {

    /**
     * Whose extractions: &#x60;mine&#x60; (uploaded by the caller, the default)
     * or &#x60;everyone&#x60;.
     */
    scope?: 'mine' | 'everyone';

    /**
     * Only extractions in these states; every state when omitted.
     * Repeat the parameter for several (&#x60;state&#x3D;ready_for_review&amp;state&#x3D;failed&#x60;).
     */
    state?: Array<ExtractionState>;
    context?: HttpContext
  }
): Observable<ExtractionList> {

    return this.listExtractions$Response(params).pipe(
      map((r: StrictHttpResponse<ExtractionList>) => r.body as ExtractionList)
    );
  }

  /**
   * Path part for operation createExtraction
   */
  static readonly CreateExtractionPath = '/invoices/extractions';

  /**
   * Upload an invoice PDF for extraction.
   *
   * Stores one invoice PDF as a new extraction in state `extracting` and
   * starts an extraction attempt in the background with the model the
   * request names, or without one with the default model: the first of
   * the enabled models (`listExtractionModels`). A model that is not
   * enabled is refused and nothing is stored. The attempt moves the extraction to `ready_for_review`,
   * with its extraction proposal and the value checks' warnings, or to
   * `failed`; each change is announced by the `extraction_changed` event.
   *
   * A PDF over 10 pages or 10 MB is refused and nothing is stored: it is
   * too large for extraction, and the sales agent fills the application
   * in by hand. So is a PDF whose pages cannot be counted within the
   * page counter's budget. A PDF in which no page tree is found (an
   * encrypted one, say) is not refused for its pages.
   *
   * Requires the `invoices:extract` permission.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `createExtraction()` instead.
   *
   * This method sends `multipart/form-data` and handles request body of type `multipart/form-data`.
   */
  createExtraction$Response(params: {
    context?: HttpContext
    body: {

/**
 * The client's invoice PDF.
 */
'pdf': Blob;

/**
 * The id of the enabled extraction model to run; the
 * default model when absent.
 */
'model'?: string;
}
  }
): Observable<StrictHttpResponse<Extraction>> {

    const rb = new RequestBuilder(this.rootUrl, ExtractionsService.CreateExtractionPath, 'post');
    if (params) {
      rb.body(params.body, 'multipart/form-data');
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json',
      context: params?.context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<Extraction>;
      })
    );
  }

  /**
   * Upload an invoice PDF for extraction.
   *
   * Stores one invoice PDF as a new extraction in state `extracting` and
   * starts an extraction attempt in the background with the model the
   * request names, or without one with the default model: the first of
   * the enabled models (`listExtractionModels`). A model that is not
   * enabled is refused and nothing is stored. The attempt moves the extraction to `ready_for_review`,
   * with its extraction proposal and the value checks' warnings, or to
   * `failed`; each change is announced by the `extraction_changed` event.
   *
   * A PDF over 10 pages or 10 MB is refused and nothing is stored: it is
   * too large for extraction, and the sales agent fills the application
   * in by hand. So is a PDF whose pages cannot be counted within the
   * page counter's budget. A PDF in which no page tree is found (an
   * encrypted one, say) is not refused for its pages.
   *
   * Requires the `invoices:extract` permission.
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `createExtraction$Response()` instead.
   *
   * This method sends `multipart/form-data` and handles request body of type `multipart/form-data`.
   */
  createExtraction(params: {
    context?: HttpContext
    body: {

/**
 * The client's invoice PDF.
 */
'pdf': Blob;

/**
 * The id of the enabled extraction model to run; the
 * default model when absent.
 */
'model'?: string;
}
  }
): Observable<Extraction> {

    return this.createExtraction$Response(params).pipe(
      map((r: StrictHttpResponse<Extraction>) => r.body as Extraction)
    );
  }

  /**
   * Path part for operation listExtractionModels
   */
  static readonly ListExtractionModelsPath = '/invoices/extractions/models';

  /**
   * List the enabled extraction models.
   *
   * The extraction models enabled in this environment, in the order the
   * model picker shows them; the first is the default. They are
   * cool-api's `EXTRACTION_MODELS` environment variable, read at startup.
   * Empty when no model is enabled, and extraction is then refused.
   *
   * Requires the `invoices:extract` permission.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `listExtractionModels()` instead.
   *
   * This method doesn't expect any request body.
   */
  listExtractionModels$Response(params?: {
    context?: HttpContext
  }
): Observable<StrictHttpResponse<ExtractionModels>> {

    const rb = new RequestBuilder(this.rootUrl, ExtractionsService.ListExtractionModelsPath, 'get');
    if (params) {
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json',
      context: params?.context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<ExtractionModels>;
      })
    );
  }

  /**
   * List the enabled extraction models.
   *
   * The extraction models enabled in this environment, in the order the
   * model picker shows them; the first is the default. They are
   * cool-api's `EXTRACTION_MODELS` environment variable, read at startup.
   * Empty when no model is enabled, and extraction is then refused.
   *
   * Requires the `invoices:extract` permission.
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `listExtractionModels$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  listExtractionModels(params?: {
    context?: HttpContext
  }
): Observable<ExtractionModels> {

    return this.listExtractionModels$Response(params).pipe(
      map((r: StrictHttpResponse<ExtractionModels>) => r.body as ExtractionModels)
    );
  }

  /**
   * Path part for operation getExtraction
   */
  static readonly GetExtractionPath = '/invoices/extractions/{id}';

  /**
   * Get an extraction.
   *
   * The extraction's state, its PDF and its latest extraction attempt,
   * with that attempt's extraction proposal once it has one.
   *
   * Requires the `invoices:extract` permission.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getExtraction()` instead.
   *
   * This method doesn't expect any request body.
   */
  getExtraction$Response(params: {
    id: number;
    context?: HttpContext
  }
): Observable<StrictHttpResponse<Extraction>> {

    const rb = new RequestBuilder(this.rootUrl, ExtractionsService.GetExtractionPath, 'get');
    if (params) {
      rb.path('id', params.id, {});
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json',
      context: params?.context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<Extraction>;
      })
    );
  }

  /**
   * Get an extraction.
   *
   * The extraction's state, its PDF and its latest extraction attempt,
   * with that attempt's extraction proposal once it has one.
   *
   * Requires the `invoices:extract` permission.
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `getExtraction$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getExtraction(params: {
    id: number;
    context?: HttpContext
  }
): Observable<Extraction> {

    return this.getExtraction$Response(params).pipe(
      map((r: StrictHttpResponse<Extraction>) => r.body as Extraction)
    );
  }

  /**
   * Path part for operation getExtractionPdf
   */
  static readonly GetExtractionPdfPath = '/invoices/extractions/{id}/pdf';

  /**
   * Get an extraction's PDF.
   *
   * The invoice PDF the extraction was created from, served inline so the
   * browser's own viewer shows it. Once the PDF is deleted
   * (`pdf_deleted_at`) it answers 410.
   *
   * Requires the `invoices:extract` permission.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getExtractionPdf()` instead.
   *
   * This method doesn't expect any request body.
   */
  getExtractionPdf$Response(params: {
    id: number;
    context?: HttpContext
  }
): Observable<StrictHttpResponse<Blob>> {

    const rb = new RequestBuilder(this.rootUrl, ExtractionsService.GetExtractionPdfPath, 'get');
    if (params) {
      rb.path('id', params.id, {});
    }

    return this.http.request(rb.build({
      responseType: 'blob',
      accept: 'application/pdf',
      context: params?.context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<Blob>;
      })
    );
  }

  /**
   * Get an extraction's PDF.
   *
   * The invoice PDF the extraction was created from, served inline so the
   * browser's own viewer shows it. Once the PDF is deleted
   * (`pdf_deleted_at`) it answers 410.
   *
   * Requires the `invoices:extract` permission.
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `getExtractionPdf$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getExtractionPdf(params: {
    id: number;
    context?: HttpContext
  }
): Observable<Blob> {

    return this.getExtractionPdf$Response(params).pipe(
      map((r: StrictHttpResponse<Blob>) => r.body as Blob)
    );
  }

  /**
   * Path part for operation getExtractionRepresentative
   */
  static readonly GetExtractionRepresentativePath = '/invoices/extractions/{id}/representative';

  /**
   * Propose an extraction's representative.
   *
   * The representative the review form proposes for the client it
   * shows, by the representative precedence: the person the latest
   * extraction proposal names, when they are an existing representative
   * of the client by email or else by full name; otherwise the client's
   * only representative; otherwise, for a new client or one with no
   * representatives, a new representative prefilled from the PDF;
   * otherwise none, and the sales agent picks. A new representative is
   * never proposed over an existing one.
   *
   * Requires the `invoices:extract` permission.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getExtractionRepresentative()` instead.
   *
   * This method doesn't expect any request body.
   */
  getExtractionRepresentative$Response(params: {
    id: number;

    /**
     * The client the form shows; absent for a new client.
     */
    client_id?: number;
    context?: HttpContext
  }
): Observable<StrictHttpResponse<ExtractionRepresentative>> {

    const rb = new RequestBuilder(this.rootUrl, ExtractionsService.GetExtractionRepresentativePath, 'get');
    if (params) {
      rb.path('id', params.id, {});
      rb.query('client_id', params.client_id, {});
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json',
      context: params?.context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<ExtractionRepresentative>;
      })
    );
  }

  /**
   * Propose an extraction's representative.
   *
   * The representative the review form proposes for the client it
   * shows, by the representative precedence: the person the latest
   * extraction proposal names, when they are an existing representative
   * of the client by email or else by full name; otherwise the client's
   * only representative; otherwise, for a new client or one with no
   * representatives, a new representative prefilled from the PDF;
   * otherwise none, and the sales agent picks. A new representative is
   * never proposed over an existing one.
   *
   * Requires the `invoices:extract` permission.
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `getExtractionRepresentative$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getExtractionRepresentative(params: {
    id: number;

    /**
     * The client the form shows; absent for a new client.
     */
    client_id?: number;
    context?: HttpContext
  }
): Observable<ExtractionRepresentative> {

    return this.getExtractionRepresentative$Response(params).pipe(
      map((r: StrictHttpResponse<ExtractionRepresentative>) => r.body as ExtractionRepresentative)
    );
  }

  /**
   * Path part for operation discardExtraction
   */
  static readonly DiscardExtractionPath = '/invoices/extractions/{id}/discard';

  /**
   * Discard an extraction.
   *
   * Discards an extraction that should never become a factoring
   * application. Discarding is a compare-and-set from `ready_for_review`
   * or `failed` to `discarded`, which is final: a discarded extraction
   * cannot be submitted or rerun. An extraction a submit is running for
   * (`submitting`) is refused.
   *
   * Requires the `invoices:extract` permission.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `discardExtraction()` instead.
   *
   * This method doesn't expect any request body.
   */
  discardExtraction$Response(params: {
    id: number;
    context?: HttpContext
  }
): Observable<StrictHttpResponse<Extraction>> {

    const rb = new RequestBuilder(this.rootUrl, ExtractionsService.DiscardExtractionPath, 'post');
    if (params) {
      rb.path('id', params.id, {});
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json',
      context: params?.context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<Extraction>;
      })
    );
  }

  /**
   * Discard an extraction.
   *
   * Discards an extraction that should never become a factoring
   * application. Discarding is a compare-and-set from `ready_for_review`
   * or `failed` to `discarded`, which is final: a discarded extraction
   * cannot be submitted or rerun. An extraction a submit is running for
   * (`submitting`) is refused.
   *
   * Requires the `invoices:extract` permission.
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `discardExtraction$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  discardExtraction(params: {
    id: number;
    context?: HttpContext
  }
): Observable<Extraction> {

    return this.discardExtraction$Response(params).pipe(
      map((r: StrictHttpResponse<Extraction>) => r.body as Extraction)
    );
  }

  /**
   * Path part for operation rerunExtraction
   */
  static readonly RerunExtractionPath = '/invoices/extractions/{id}/rerun';

  /**
   * Rerun an extraction with a model.
   *
   * Starts a new extraction attempt over the extraction's PDF with the
   * given enabled model, for a sales agent who finds the latest
   * proposal poor. Rerunning is a compare-and-set from
   * `ready_for_review` or `failed` back to `extracting`; the new
   * attempt is stored beside the earlier ones, each with its model, and
   * becomes the latest attempt. The review page warns before it that
   * the agent's corrections will be lost.
   *
   * A workflow that cannot be started fails the new attempt, as an
   * upload's does, rather than the request.
   *
   * Requires the `invoices:extract` permission.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `rerunExtraction()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  rerunExtraction$Response(params: {
    id: number;
    context?: HttpContext
    body: RerunExtractionRequest
  }
): Observable<StrictHttpResponse<Extraction>> {

    const rb = new RequestBuilder(this.rootUrl, ExtractionsService.RerunExtractionPath, 'post');
    if (params) {
      rb.path('id', params.id, {});
      rb.body(params.body, 'application/json');
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json',
      context: params?.context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<Extraction>;
      })
    );
  }

  /**
   * Rerun an extraction with a model.
   *
   * Starts a new extraction attempt over the extraction's PDF with the
   * given enabled model, for a sales agent who finds the latest
   * proposal poor. Rerunning is a compare-and-set from
   * `ready_for_review` or `failed` back to `extracting`; the new
   * attempt is stored beside the earlier ones, each with its model, and
   * becomes the latest attempt. The review page warns before it that
   * the agent's corrections will be lost.
   *
   * A workflow that cannot be started fails the new attempt, as an
   * upload's does, rather than the request.
   *
   * Requires the `invoices:extract` permission.
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `rerunExtraction$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  rerunExtraction(params: {
    id: number;
    context?: HttpContext
    body: RerunExtractionRequest
  }
): Observable<Extraction> {

    return this.rerunExtraction$Response(params).pipe(
      map((r: StrictHttpResponse<Extraction>) => r.body as Extraction)
    );
  }

  /**
   * Path part for operation submitExtraction
   */
  static readonly SubmitExtractionPath = '/invoices/extractions/{id}/submit';

  /**
   * Submit an extraction.
   *
   * Submits the values a sales agent confirmed from an extraction as a
   * factoring application. It builds an Extracted Finvoice from the
   * confirmed seller, end customer and invoice values (the seller as the
   * Finvoice seller, the end customer as its buyer), validates it
   * against the Finvoice 3.0 schema, and sends it with the extraction's
   * PDF through the factoring application intake, which stores it as the
   * invoice's `Extracted Finvoice` attachment (never as its Original
   * Invoice Finvoice) and reads it as it reads a partner's Finvoice: the
   * client in `metadata` must be the Finvoice seller (by business ID)
   * and its `iban` the seller's IBAN. The original reference number is
   * carried onto the invoice only when it is a valid Finnish or RF
   * reference.
   *
   * Submitting first claims the extraction, a compare-and-set from
   * `ready_for_review` or `failed` to `submitting`, so a concurrent
   * submit, discard or rerun is refused at once; a failed extraction can
   * be filled in by hand and submitted. The intake then runs, and the
   * extraction moves to `submitted`, recording the invoice it created,
   * the attempt named by `attempt_id` and the values submitted. An intake
   * that refuses the values gives the extraction back its earlier state.
   *
   * Requires the `invoices:extract` permission.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `submitExtraction()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  submitExtraction$Response(params: {
    id: number;
    context?: HttpContext
    body: SubmitExtractionRequest
  }
): Observable<StrictHttpResponse<Extraction>> {

    const rb = new RequestBuilder(this.rootUrl, ExtractionsService.SubmitExtractionPath, 'post');
    if (params) {
      rb.path('id', params.id, {});
      rb.body(params.body, 'application/json');
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json',
      context: params?.context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<Extraction>;
      })
    );
  }

  /**
   * Submit an extraction.
   *
   * Submits the values a sales agent confirmed from an extraction as a
   * factoring application. It builds an Extracted Finvoice from the
   * confirmed seller, end customer and invoice values (the seller as the
   * Finvoice seller, the end customer as its buyer), validates it
   * against the Finvoice 3.0 schema, and sends it with the extraction's
   * PDF through the factoring application intake, which stores it as the
   * invoice's `Extracted Finvoice` attachment (never as its Original
   * Invoice Finvoice) and reads it as it reads a partner's Finvoice: the
   * client in `metadata` must be the Finvoice seller (by business ID)
   * and its `iban` the seller's IBAN. The original reference number is
   * carried onto the invoice only when it is a valid Finnish or RF
   * reference.
   *
   * Submitting first claims the extraction, a compare-and-set from
   * `ready_for_review` or `failed` to `submitting`, so a concurrent
   * submit, discard or rerun is refused at once; a failed extraction can
   * be filled in by hand and submitted. The intake then runs, and the
   * extraction moves to `submitted`, recording the invoice it created,
   * the attempt named by `attempt_id` and the values submitted. An intake
   * that refuses the values gives the extraction back its earlier state.
   *
   * Requires the `invoices:extract` permission.
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `submitExtraction$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  submitExtraction(params: {
    id: number;
    context?: HttpContext
    body: SubmitExtractionRequest
  }
): Observable<Extraction> {

    return this.submitExtraction$Response(params).pipe(
      map((r: StrictHttpResponse<Extraction>) => r.body as Extraction)
    );
  }

}
